'use client';

/**
 * Home Page - Jojo & Jocelyn Complete Upholstery And Curtain Services
 * Luxury portfolio-driven landing page
 */

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { BeforeAfter } from '@/components/before-after';
import { HeroCarousel } from '@/components/hero-carousel';
import { whyChooseUs, testimonials, portfolioProjects } from '@/data/portfolio';
import * as Icons from 'lucide-react';
import { Phone, Mail, ArrowRight, Star, LucideIcon } from 'lucide-react';

export default function HomePage() {
  const featuredProjects = portfolioProjects.filter((project) => project.featured && project.beforeImage && project.afterImage);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Carousel */}
        <HeroCarousel isBackground={true} />

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-20 text-center text-white">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-wide mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000 drop-shadow-lg">
            <span className="text-yellow-300">Luxury Upholstery</span><br />
            <span className="text-yellow-300 drop-shadow-lg">Crafted with Passion</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-12 leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 drop-shadow-md">
            Expert furniture restoration and custom window treatments that transform your space.
            Serving homeowners and interior designers with uncompromising quality since 1999.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 shadow-2xl hover:shadow-primary/50 transition-all duration-300"
              asChild
            >
              <Link href="/contact">
                <Mail className="mr-2 h-5 w-5" />
                Get a Free Quote
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6 transition-all duration-300"
              asChild
            >
              <Link href="tel:09176260243">
                <Phone className="mr-2 h-5 w-5" />
                Call Us Today
              </Link>
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-16 flex flex-wrap justify-center gap-8 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-primary text-primary" />
              <span>25+ Years Experience</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-primary text-primary" />
              <span>1000+ Projects Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-primary text-primary" />
              <span>100% Satisfaction Guaranteed</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full p-1">
            <div className="w-1.5 h-2 bg-white/50 rounded-full mx-auto animate-pulse" />
          </div>
        </div>
      </section>


      {/* Featured Portfolio */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Transformations That Inspire
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See how we've helped clients transform their furniture and spaces with
              our expert craftsmanship and attention to detail.
            </p>
          </div>

          {/* Featured Large Transformation 1 */}
          <div className="mb-16 max-w-5xl mx-auto">
            <BeforeAfter
              beforeImage="/images/uploads/2a34977c-d234-47a6-88fc-d778b41c0df5.jpg"
              afterImage="/images/uploads/41c57ade-4e18-4c3b-bf84-e25684efdbb4.jpg"
              title="Outdoor Sofa Restoration - Complete Revival"
              size="large"
            />
          </div>

          {/* Featured Large Transformation 2 */}
          <div className="mb-16 max-w-5xl mx-auto">
            <BeforeAfter
              beforeImage="/images/uploads/583b61e6-027a-4b67-8155-9b79d63a7f66.jpg"
              afterImage="/images/uploads/ba503849-ccba-4e2b-b0c6-1df2e8d67187.jpg"
              title="Living Room Sectional - Premium Reupholstery"
              size="large"
            />
          </div>


          <div className="text-center">
            <Button size="lg" variant="outline" className="border-2" asChild>
              <Link href="/portfolio">
                View Complete Portfolio
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose Jojo & Jocelyn?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our commitment to excellence, combined with decades of experience,
              makes us the trusted choice for discerning clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = (Icons as any)[item.icon] as LucideIcon || Icons.Circle;
              return (
                <div
                  key={index}
                  className="p-8 rounded-lg border-2 border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="mb-4 p-3 bg-primary/10 rounded-lg w-fit">
                    <IconComponent className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-foreground text-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-background/80 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied clients have to say.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.slice(0, 4).map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 bg-background/5 backdrop-blur rounded-lg border border-background/20 hover:bg-background/10 transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-background/90 leading-relaxed mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-background/20 pt-4">
                  <p className="font-semibold text-background">{testimonial.name}</p>
                  <p className="text-sm text-background/70">{testimonial.role}</p>
                  <p className="text-xs text-background/50 mt-1">{testimonial.projectType}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">
            Let's discuss your project. Whether it's a treasured heirloom or a modern makeover,
            we're here to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary text-lg px-8 py-6"
              asChild
            >
              <Link href="/contact">
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 text-lg px-8 py-6"
              asChild
            >
              <Link href="/portfolio">
                View Our Work
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
